import type { ScopedChannel } from '@/types/auth'
import type { ChannelWithStats } from '@/types/channel'

/** O que o popover da marca precisa de um canal: identidade + aparência (opcional). */
export interface BrandChannel {
  db_id: string
  name: string
  /** Slug lucide kebab; `null` = sem ícone (o `ChannelAvatar` cai em `Antenna`). */
  icon: string | null
  /** Hex #RRGGBB; `null` = sem cor (gradiente default do `ChannelAvatar`). */
  color: string | null
  /** Contadores do canal. `0` quando a lista HTTP ainda não resolveu — o `/auth/me`
   *  não entrega contador nenhum, e badge ausente é degradação aceita. */
  chats_count: number
  waiting_chats: number
  total_unread_messages: number
}

/**
 * Junta as duas listas de canais do usuário para o popover da marca, no mesmo molde
 * defensivo do `mergeChannelStats` (`src/providers/ChatBotContext.tsx`).
 *
 * `scoped` (o snapshot do `/auth/me`, hidratado SÍNCRONO do localStorage) é a fonte de
 * identidade: sobrevive a `GET /whatsapp/channels` lento ou fora do ar. `incoming` (a
 * query HTTP) só acrescenta `icon`/`color`, que o `ScopedChannel` não tem.
 *
 * Incoming vazio ou ausente NUNCA apaga a lista — sem isso, o atendente de vários canais
 * fica sem nenhuma forma de trocar de canal pelo rail na primeira falha sem cache.
 * O contrário também vale: canal que só existe no incoming (grant novo que o `/auth/me`
 * ainda não refletiu) entra defensivamente no fim.
 */
export function mergeBrandChannels(
  scoped: ScopedChannel[] | undefined,
  incoming: ChannelWithStats[] | undefined,
): BrandChannel[] {
  const base = scoped ?? []
  const list = incoming ?? []
  const appearanceByDbId = new Map(list.map((channel) => [channel.db_id, channel]))

  const merged = base.map((channel) => {
    const appearance = appearanceByDbId.get(channel.db_id)
    return {
      db_id: channel.db_id,
      // Nome vem do auth de propósito: o incoming é enriquecimento, não identidade
      // (mesma regra do `mergeChannelStats`, que não sobrescreve nome/descrição).
      name: channel.name,
      icon: appearance?.icon ?? null,
      color: appearance?.color ?? null,
      chats_count: appearance?.chats_count ?? 0,
      waiting_chats: appearance?.waiting_chats ?? 0,
      total_unread_messages: appearance?.total_unread_messages ?? 0,
    }
  })

  const known = new Set(base.map((channel) => channel.db_id))
  const onlyInIncoming = list
    .filter((channel) => !known.has(channel.db_id))
    .map((channel) => ({
      db_id: channel.db_id,
      name: channel.name,
      icon: channel.icon,
      color: channel.color,
      chats_count: channel.chats_count,
      waiting_chats: channel.waiting_chats,
      total_unread_messages: channel.total_unread_messages,
    }))

  return [...merged, ...onlyInIncoming]
}

/**
 * Total de mensagens não lidas do escopo. É **estado**, não notificação: nenhuma
 * preferência o desliga (I4, mesma regra do badge do rail em `prefs.ts:23-25`).
 *
 * Existe como função porque três superfícies leem o mesmo número — o badge do
 * rail, o badge do ícone do app e o título da janela.
 */
export function sumUnread(channels: BrandChannel[]): number {
  return channels.reduce((soma, canal) => soma + canal.total_unread_messages, 0)
}
